
import { motion } from "framer-motion";
import { Product } from "@/types/common";
import { Star, Crown, Award, Sparkles, Timer } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface LuxuryProductHeaderProps {
  product: Product;
}

const LuxuryProductHeader = ({ product }: LuxuryProductHeaderProps) => {
  const isLowStock = product.stock_quantity !== null && product.stock_quantity <= 5;
  const isOutOfStock = product.stock_quantity === 0;
  const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;
  const discountPercentage = hasDiscount 
    ? Math.round(((product.compare_at_price! - product.price) / product.compare_at_price!) * 100)
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="space-y-6"
    >
      {/* Luxury Category & Badges */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-yellow-500" />
          <span className="font-playfair text-sm font-medium text-muted-foreground uppercase tracking-wider">
            {product.categories?.name || 'Premium Collection'}
          </span>
        </div>
        
        <div className="flex gap-2">
          {product.featured && (
            <Badge className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white border-0">
              <Crown className="w-3 h-3 mr-1" />
              Premium
            </Badge>
          )}
          {hasDiscount && (
            <Badge className="bg-gradient-to-r from-red-500 to-pink-500 text-white border-0 animate-pulse">
              -{discountPercentage}% OFF
            </Badge>
          )}
          {isLowStock && !isOutOfStock && (
            <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white border-0">
              <Timer className="w-3 h-3 mr-1" />
              Limited Stock
            </Badge>
          )}
        </div>
      </div>

      {/* Luxury Product Title */}
      <motion.h1 
        className="font-playfair text-4xl lg:text-5xl font-bold bg-gradient-to-br from-foreground via-foreground to-foreground/80 bg-clip-text text-transparent leading-tight"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2, duration: 0.6 }}
      >
        {product.name}
      </motion.h1>
      
      {/* Premium Rating System */}
      <motion.div 
        className="flex items-center gap-6"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        <div className="flex items-center gap-2">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 + (i * 0.1), duration: 0.3 }}
              >
                <Star className="h-5 w-5 text-yellow-400 fill-yellow-400 drop-shadow-sm" />
              </motion.div>
            ))}
          </div>
          <span className="text-sm font-medium text-muted-foreground">(4.9)</span>
        </div>
        
        <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-950 dark:to-emerald-900 rounded-full">
          <Award className="h-4 w-4 text-emerald-600" />
          <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">128 Reviews</span>
        </div>
      </motion.div>

      {/* Luxury Price Display */}
      <motion.div 
        className="space-y-2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.6 }}
      >
        <div className="flex items-baseline gap-4">
          <span className="font-playfair text-5xl font-bold bg-gradient-to-r from-primary via-primary to-primary/80 bg-clip-text text-transparent">
            ${product.price}
          </span>
          {hasDiscount && (
            <span className="text-2xl text-muted-foreground line-through decoration-red-500">
              ${product.compare_at_price}
            </span>
          )}
        </div>
        
        {hasDiscount && (
          <motion.div
            className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-950 dark:to-pink-950 rounded-full"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <span className="text-sm font-semibold text-red-600 dark:text-red-400">
              You save ${(product.compare_at_price! - product.price).toFixed(2)}
            </span>
          </motion.div>
        )}
      </motion.div>

      {/* Trust Signals */}
      <motion.div 
        className="flex flex-wrap gap-4 pt-4 border-t border-border/50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.5 }}
      >
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          Free shipping worldwide
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
          30-day return policy
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
          1-year warranty
        </div>
      </motion.div>
    </motion.div>
  );
};

export default LuxuryProductHeader;
